import { useEffect, useRef, useState } from 'react';

/**
 * Card Timer — overlay surface for Streamloots-style "no cussing for 5 minutes"
 * cards.
 *
 * Two data sources feed the active-timer list:
 *   1. Mount-time GET /api/overlays/timers backfills anything ticking when the
 *      browser source loads (refresh, scene swap).
 *   2. The 'card-timer' socket event (forwarded by ws-gateway from Redis
 *      streamer:<id>:card-timer) carries {type: 'start' | 'stop', ...}.
 *
 * Display config (fields):
 *   accentColor: string         (default '#a78bfa')
 *   layout: 'stack' | 'inline'  (default 'stack')
 *   title: string               (default '' — header hidden when empty)
 *   maxRows: number             (default 6)
 *
 * The widget runs a local 500ms tick — no server-side fan-out needed.
 */

export interface CardTimerEvent {
  type: 'start' | 'stop';
  id: string;
  label?: string;
  durationSeconds?: number;
  startedAt?: string;
  endsAt?: string;
  triggeredBy?: string | null;
  cardName?: string | null;
  rarity?: string | null;
}

interface ActiveTimer {
  id: string;
  label: string;
  durationSeconds: number;
  startedAtMs: number;
  endsAtMs: number;
  triggeredBy?: string | null;
}

function fmt(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec}s`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function CardTimerWidget({
  fields,
  token,
  event,
}: {
  fields: Record<string, unknown>;
  token?: string;
  event?: CardTimerEvent | null;
}) {
  const accent = String(fields.accentColor ?? '#a78bfa');
  const layout = (fields.layout as 'stack' | 'inline' | undefined) ?? 'stack';
  const title = String(fields.title ?? '');
  const maxRows = Number(fields.maxRows ?? 6);

  const [timers, setTimers] = useState<ActiveTimer[]>([]);
  const [now, setNow] = useState(Date.now());
  const lastEventRef = useRef<CardTimerEvent | null>(null);

  // Mount-time backfill. Without this an OBS browser-source refresh
  // mid-timer would show nothing until the next start event.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/overlays/timers?token=${encodeURIComponent(token)}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          timers: Array<{
            id: string;
            label: string;
            durationSeconds: number;
            startedAt: string;
            endsAt: string;
            triggeredBy?: string | null;
          }>;
        };
        if (cancelled) return;
        setTimers((prev) => {
          const byId = new Map(prev.map((t) => [t.id, t]));
          for (const t of data.timers) {
            byId.set(t.id, {
              id: t.id,
              label: t.label,
              durationSeconds: t.durationSeconds,
              startedAtMs: new Date(t.startedAt).getTime(),
              endsAtMs: new Date(t.endsAt).getTime(),
              triggeredBy: t.triggeredBy ?? null,
            });
          }
          return Array.from(byId.values());
        });
      } catch {
        // Quietly ignore — the widget still works via socket events.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Apply incoming start/stop events from the socket.
  useEffect(() => {
    if (!event || event === lastEventRef.current) return;
    lastEventRef.current = event;
    if (event.type === 'start' && event.endsAt && event.label && event.durationSeconds) {
      const incoming: ActiveTimer = {
        id: event.id,
        label: event.label,
        durationSeconds: event.durationSeconds,
        startedAtMs: event.startedAt ? new Date(event.startedAt).getTime() : Date.now(),
        endsAtMs: new Date(event.endsAt).getTime(),
        triggeredBy: event.triggeredBy ?? null,
      };
      setTimers((prev) => {
        const filtered = prev.filter((t) => t.id !== incoming.id);
        return [...filtered, incoming];
      });
    } else if (event.type === 'stop') {
      setTimers((prev) => prev.filter((t) => t.id !== event.id));
    }
  }, [event]);

  // Local tick — drives countdown text + bar fill.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  // Drop expired timers from state so the next start doesn't render alongside
  // a dead row left over from before the unmount.
  useEffect(() => {
    setTimers((prev) => {
      const live = prev.filter((t) => t.endsAtMs > now);
      return live.length === prev.length ? prev : live;
    });
  }, [now]);

  const active = timers.filter((t) => t.endsAtMs > now);
  active.sort((a, b) => a.endsAtMs - b.endsAtMs);
  const visible = active.slice(0, maxRows);

  if (visible.length === 0) return null;

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(10,8,24,0.92), rgba(18,14,38,0.92))',
        border: `1px solid ${accent}40`,
        borderRadius: 12,
        padding: '10px 14px',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        minWidth: layout === 'inline' ? undefined : 260,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 20,
          right: 20,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      {title && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.5)',
            marginBottom: 8,
          }}
        >
          {title}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: layout === 'inline' ? 'row' : 'column',
          flexWrap: layout === 'inline' ? 'wrap' : 'nowrap',
          gap: 6,
        }}
      >
        {visible.map((t) => {
          const remaining = t.endsAtMs - now;
          const total = t.durationSeconds * 1000;
          const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
          const colorBase = accent;
          return (
            <div
              key={t.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px 6px 7px',
                borderRadius: 8,
                background: `linear-gradient(90deg, ${colorBase}20, transparent)`,
                border: `1px solid ${colorBase}33`,
                position: 'relative',
                minWidth: layout === 'inline' ? 150 : 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 8,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, ${colorBase}2a, transparent)`,
                    transition: 'width 500ms linear',
                  }}
                />
              </div>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${colorBase}, ${colorBase}aa)`,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 12,
                  flexShrink: 0,
                  zIndex: 1,
                }}
              >
                ⏱
              </span>
              <div style={{ flex: 1, minWidth: 0, zIndex: 1 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t.label}
                </div>
                {t.triggeredBy && (
                  <div
                    style={{
                      fontSize: 10,
                      color: 'rgba(255,255,255,0.55)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    by {t.triggeredBy}
                  </div>
                )}
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: colorBase,
                  fontVariantNumeric: 'tabular-nums',
                  zIndex: 1,
                  flexShrink: 0,
                }}
              >
                {fmt(remaining)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
